import {
  PrimaryCta,
  Section,
  SectionHeading,
  SecondaryCta,
  Seo,
} from '@/components/primitives'

type Integration = {
  name: string
  detail: string
  logo: string
  logoAlt?: string
}

const GROUPS: { title: string; heading: string; items: Integration[] }[] = [
  {
    title: 'Quoting and enrollment',
    heading: 'From first quote to signed policy.',
    items: [
      {
        name: 'Sunfire',
        detail: 'Quote and enroll Medicare plans from the same record.',
        logo: 'sunfire',
      },
      {
        name: 'HealthSherpa',
        detail: 'Quote and enroll ACA plans with the customer journey attached.',
        logo: 'healthsherpa',
      },
      {
        name: 'Ideon',
        detail: 'Pull eligibility and plan data into your workflows.',
        logo: 'ideon',
      },
      {
        name: 'Connecture',
        detail: 'Medicare Advantage quoting inside your sales flow.',
        logo: 'connecture',
      },
    ],
  },
  {
    title: 'Agency systems',
    heading: 'Your book of business stays connected.',
    items: [
      {
        name: 'Vertafore',
        detail: 'Connect your agency management system and carrier feeds.',
        logo: 'vertafore',
      },
    ],
  },
  {
    title: 'Coaching and compliance',
    heading: 'Help during the call. Checks before it.',
    items: [
      {
        name: 'TLIntel',
        detail:
          'Built-in call QA. Checks your calls for required disclosures and script deviations, where enabled.',
        logo: 'tlintel',
      },
      {
        name: 'Balto',
        detail: 'Live coaching during the call.',
        logo: 'balto',
      },
      {
        name: 'Fluents',
        detail: 'AI voice for approved outreach, inside your rules.',
        logo: 'fluents',
      },
      {
        name: 'Contact Center Compliance',
        detail: 'Scrubs Do-Not-Call and litigator numbers before dialing.',
        logo: 'ccc',
      },
      {
        name: 'OutboundANI',
        detail: 'Protects your caller-ID reputation on outbound campaigns.',
        logo: 'outboundani',
      },
    ],
  },
  {
    title: 'Documents and marketing',
    heading: 'Signatures and follow-ups without extra seats.',
    items: [
      {
        name: 'Native e-signature',
        detail: 'Built in. No DocuSign, no PandaDoc, no extra seat fee.',
        logo: 'native-esign',
      },
      {
        name: 'Klaviyo + KLaunch',
        detail: 'Remarketing and text follow-ups tied to results.',
        logo: 'klaviyo',
      },
    ],
  },
  {
    title: 'Build your own',
    heading: 'Your tools, your way.',
    items: [
      {
        name: 'Embedded quoting and carrier links',
        detail: 'Jump to quoting and carrier sites straight from the record.',
        logo: 'embedded-quoting',
      },
      {
        name: 'Open API',
        detail: 'REST, webhooks, and an event bus inside your account scope.',
        logo: 'open-api',
      },
    ],
  },
]

function IntegrationLogo({ slug, name }: { slug: string; name: string }) {
  return (
    <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-sm border border-base-line bg-base-900 p-1.5">
      <img
        src={`/images/integrations/${slug}-128.webp`}
        srcSet={`/images/integrations/${slug}-64.webp 64w, /images/integrations/${slug}-128.webp 128w, /images/integrations/${slug}-256.webp 256w`}
        sizes="48px"
        width={48}
        height={48}
        alt=""
        className="h-full w-full object-contain"
        loading="lazy"
        decoding="async"
      />
      <span className="sr-only">{name} logo</span>
    </span>
  )
}

export default function Integrations() {
  return (
    <>
      <Seo
        title="Integrations | TLDNXS Customer Operations Platform"
        description="Your tools plug in. The operation stays one: quoting, enrollment, coaching, compliance, and signatures on the same records."
      />

      <section aria-labelledby="integrations-title" className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="field-grid pointer-events-none absolute inset-0 opacity-50 [mask-image:radial-gradient(70%_60%_at_50%_20%,black,transparent)]"
        />
        <div className="relative mx-auto w-full max-w-7xl px-5 py-20 sm:px-8 md:py-28">
          <div className="mx-auto max-w-3xl text-center">
          <p className="eyebrow">Integrations</p>
          <h1
            id="integrations-title"
            className="mt-5 text-4xl font-extrabold leading-[1.05] tracking-tight text-ink sm:text-5xl"
          >
            Your tools plug in. The operation stays one.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-ink-mute md:text-lg">
            Quoting, enrollment, coaching, compliance, signatures: connected to the same customer
            records. Nothing becomes a second source of truth.
          </p>
          <p className="mt-6 text-sm text-ink-mute">
            Not listed? If it has an API, we can wire it. We&rsquo;ll map the path on a walkthrough.
          </p>
          </div>
        </div>
      </section>

      {GROUPS.map((group, gi) => (
        <Section
          key={group.title}
          labelledBy={`group-${gi}-heading`}
          className={gi % 2 === 0 ? 'bg-base-900/40' : ''}
        >
          <SectionHeading
            id={`group-${gi}-heading`}
            eyebrow={group.title}
            eyebrowTone="mute"
            title={group.heading}
          />
          <ul className="mt-12 grid gap-3 sm:grid-cols-2">
            {group.items.map((item) => (
              <li
                key={item.name}
                className="flex gap-4 rounded-sm border border-base-line bg-base-850 p-5"
              >
                <IntegrationLogo slug={item.logo} name={item.name} />
                <div className="min-w-0">
                  <p className="text-sm font-extrabold tracking-tight text-ink">{item.name}</p>
                  <p className="mt-1.5 text-sm leading-relaxed text-ink-mute">{item.detail}</p>
                </div>
              </li>
            ))}
          </ul>
        </Section>
      ))}

      <Section labelledBy="wire-heading" className="bg-base-900/40">
        <div className="mx-auto max-w-3xl text-center">
          <h2
            id="wire-heading"
            className="text-3xl font-extrabold tracking-tight text-ink sm:text-4xl"
          >
            Not listed? If it has an API, we can wire it.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-ink-mute md:text-lg">
            Bring your stack. We will map the connections live.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <PrimaryCta to="/walkthrough">Book a walkthrough</PrimaryCta>
            <SecondaryCta to="/platform#extensibility">Platform extensibility</SecondaryCta>
          </div>
        </div>
      </Section>
    </>
  )
}
